import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import type { ComponentLike } from '@glint/template';
import Button from 'tio-ui/components/button';
import Heading from 'tio-ui/components/heading';
import PageHero from 'tio-ui/components/page-hero';

interface HeroHeaderSignature {
  Args: {
    greeting: string;
    icon: ComponentLike<{ Element: SVGElement }>;
    title: string;
    actionLabel?: string;
    actionRoute?: string;
    actionKind?: HeroActionKind;
  };
  Blocks: { default?: [] };
}

/** How the hero's top-right action renders: Figma's text link, primary CTA, or secondary CTA. */
export type HeroActionKind = 'link' | 'primary' | 'secondary';

const HeroHeader: TOC<HeroHeaderSignature> = <template>
  <div class="tio-row-start-between">
    <div class="max-w-2xl">
      <div class="tio-row-tight tio-text-sm-semibold app-text-brand">
        {{#let @icon as |Icon|}}
          <Icon class="tio-icon-sm" />
        {{/let}}
        {{! The greeting usually carries the viewer's name, so session replay masks it. }}
        <span class="pii">{{@greeting}}</span>
      </div>
      <Heading @level={{1}}>{{@title}}</Heading>
      {{yield}}
    </div>
    {{#if @actionRoute}}
      {{#if @actionLabel}}
        {{#if (eq @actionKind "primary")}}
          <Button @route={{@actionRoute}} @intent="primary" @size="sm" class="shrink-0">
            {{@actionLabel}}
          </Button>
        {{else if (eq @actionKind "secondary")}}
          <Button
            @route={{@actionRoute}}
            @intent="primary"
            @appearance="outlined"
            @size="sm"
            class="shrink-0"
          >
            {{@actionLabel}}
          </Button>
        {{else}}
          <LinkTo
            @route={{@actionRoute}}
            class="tio-text-sm-semibold whitespace-nowrap app-text-link app-text-link--icon"
          >
            {{@actionLabel}}
          </LinkTo>
        {{/if}}
      {{/if}}
    {{/if}}
  </div>
</template>;

interface DashboardHeroSignature {
  Args: {
    /** Eyebrow line above the title, e.g. a time-of-day greeting with the viewer's name. */
    greeting: string;
    /** Glyph shown before the greeting. */
    icon: ComponentLike<{ Element: SVGElement }>;
    /** The page's H1. */
    title: string;
    /** Text of the top-right link. The link renders only when this and `@actionRoute` are both set. */
    actionLabel?: string;
    /** Route the top-right link opens; the link takes no model. */
    actionRoute?: string;
    /** `link` (default) is a text link; `primary` and `secondary` render the Figma CTA buttons. */
    actionKind?: HeroActionKind;
  };
  Blocks: {
    /** Content under the title, such as a subtitle or a call to action. */
    default?: [];
    /** Stat tiles under the header; the caller owns the grid layout. */
    stats?: [];
  };
  Element: HTMLDivElement;
}

/**
 * Brand-gradient dashboard hero: greeting eyebrow, title, optional top-right
 * link, and an optional row of stat tiles.
 */
const DashboardHero: TOC<DashboardHeroSignature> = <template>
  <PageHero @gradient="brand-soft" class="tio-elevation-card-raised" ...attributes>
    <:default>
      {{! Without stats the header sits directly in the hero, with no column wrapper. }}
      {{#let (element (if (has-block "stats") "div" "")) as |Column|}}
        <Column class="tio-col-extra-loose">
          <HeroHeader
            @greeting={{@greeting}}
            @icon={{@icon}}
            @title={{@title}}
            @actionLabel={{@actionLabel}}
            @actionRoute={{@actionRoute}}
            @actionKind={{@actionKind}}
          >{{yield}}</HeroHeader>
          {{yield to="stats"}}
        </Column>
      {{/let}}
    </:default>
  </PageHero>
</template>;

export default DashboardHero;
